import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activities } from '../data';
import { ActivityCard } from '../components/ui/Cards';
import './DetailPage.css';

export default function ActivityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const activity = activities.find(a => a.slug === slug);

  const [adults, setAdults] = useState(2);
  const [date, setDate] = useState('');

  if (!activity) return (
    <div className="not-found-msg container">
      Activity not found. <Link to="/activities">Back to activities</Link>
    </div>
  );

  const total = adults * activity.price;
  const related = activities.filter(a => a.type === activity.type && a.id !== activity.id).slice(0, 3);
  const { t } = useTranslation();

  const handleBook = () => {
    navigate(`/book?activity=${activity.slug}&adults=${adults}&date=${date}`);
  };

  const heroImages = {
    'hot-air-balloon-marrakech':        'https://images.unsplash.com/photo-1549740425-5e9ed4d8cd34?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'camel-ride-palmeraie':             'https://images.unsplash.com/photo-1640328504956-e6fb38ff8934?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'camel-ride-agafay':                'https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=600&q=80',
    'quad-palmeraie':                   'https://images.unsplash.com/photo-1627760444690-59f0a5fccee6?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'quad-agafay':                      'https://images.unsplash.com/photo-1624062999803-976e1adc8ea2?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'buggy-agafay':                     'https://images.unsplash.com/photo-1635350644558-0f12a6a88cbd?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'horseback-riding-lake-takerkoust': 'https://images.unsplash.com/photo-1633767979501-6225d151ba70?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'marrakech-cooking-class':          'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&q=80',
    'pottery-class-marrakech':          'https://images.unsplash.com/photo-1612950662573-546117a61e78?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'sidecar-tour-marrakech':           'https://www.image2url.com/r2/default/images/1776895483967-ebd12bf7-9ca3-4d7a-b494-dc16d81b6024.webp',
    'marrakech-hammam-spa':             'https://images.unsplash.com/photo-1714648775497-9f54f75eb3ea?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'yoga-marrakech':                   'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1600&q=80',
  };
  const heroImg = heroImages[activity.slug];

  return (
    <>
      <div className="detail-hero">
        <div className="detail-hero__img" style={{ background: activity.bgColor }}>
          {heroImg
            ? <img src={heroImg} alt={activity.name} />
            : <div style={{ width:'100%', height:'100%', background: `linear-gradient(135deg, ${activity.bgColor}, #1a1a2e)` }} />
          }
          {activity.badge && <span className="badge badge-dark detail-hero__badge">{activity.badge}</span>}
        </div>
      </div>

      <div className="container detail-layout">
        <div className="detail-main">
          <div className="breadcrumb">
            <Link to="/">{t('common.home')}</Link> <span>/</span>
            <Link to="/activities">{t('nav.activities')}</Link> <span>/</span>
            <span>{activity.name}</span>
          </div>

          <div className="detail-header">
            <span className="badge badge-gold">{activity.location} · {activity.type}</span>
            <h1 className="detail-title">{activity.name}</h1>
            <div className="detail-meta">
              <span className="stars">{'★'.repeat(Math.floor(activity.rating))}</span>
              <span className="detail-meta-text">{activity.rating} · {activity.reviews} reviews</span>
              <span className="detail-meta-sep">·</span>
              <span className="detail-meta-text">⏱ {activity.duration}</span>
              {activity.minAge && <><span className="detail-meta-sep">·</span><span className="detail-meta-text">👤 Age {activity.minAge}+</span></>}
              <span className="detail-meta-sep">·</span>
              <span className="detail-meta-text">💪 {activity.fitnessLevel}</span>
            </div>
          </div>

          <p className="detail-desc">{activity.description}</p>

          <h3 className="detail-sub-title">{t('detail.topHighlights')}</h3>
          <ul className="highlight-list">
            {activity.highlights.map((h, i) => (
              <li key={i}><span className="highlight-dot" />{h}</li>
            ))}
          </ul>

          <div className="quick-facts">
            <div className="quick-fact"><span className="quick-fact__label">{t('detail.duration')}</span><span className="quick-fact__val">{activity.duration}</span></div>
            <div className="quick-fact"><span className="quick-fact__label">{t('detail.location')}</span><span className="quick-fact__val">{activity.location}</span></div>
            <div className="quick-fact"><span className="quick-fact__label">{t('detail.minAge')}</span><span className="quick-fact__val">{activity.minAge ? `${activity.minAge} ${t('common.years')}` : t('common.any')}</span></div>
            <div className="quick-fact"><span className="quick-fact__label">{t('detail.fitnessLevel')}</span><span className="quick-fact__val">{activity.fitnessLevel}</span></div>
          </div>

          <div style={{ marginTop: 32 }}>
            <div className="inc-exc-grid">
              <div>
                <h3 className="detail-sub-title included">{t('detail.whatIncluded')}</h3>
                <ul className="inc-list inc">
                  {activity.included.map((item, i) => <li key={i}><span>✓</span>{item}</li>)}
                </ul>
              </div>
              <div>
                <h3 className="detail-sub-title excluded">{t('detail.notIncluded')}</h3>
                <ul className="inc-list exc">
                  {activity.excluded.map((item, i) => <li key={i}><span>✗</span>{item}</li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="meeting-point" style={{ marginTop: 32 }}>
            <strong>{t('detail.meetingPoint')}:</strong> {activity.meetingPoint}
          </div>
        </div>

        <div className="detail-sidebar">
          <div className="booking-card">
            <div className="booking-card__stars">
              <span className="stars">{'★'.repeat(Math.floor(activity.rating))}</span>
              <span className="booking-card__reviews">{activity.reviews} reviews</span>
            </div>

            <div className="booking-field">
              <label>{t('booking.date')}</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
            </div>

            <div className="booking-field">
              <label>{t('detail.participants')}</label>
              <div className="counter">
                <button onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
                <span>{adults}</span>
                <button onClick={() => setAdults(adults + 1)}>+</button>
              </div>
            </div>

            <div className="booking-card__total">
              <span>{t('booking.price')}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>{t('booking.onRequest')}</span>
            </div>

            <button className="btn btn-primary booking-card__cta" onClick={handleBook}>
              {t('booking.bookNow')}
            </button>

            <a
              href={`https://wa.me/212694401220?text=Hi!%20I'd%20like%20to%20book:%20${encodeURIComponent(activity.name)}`}
              target="_blank" rel="noreferrer"
              className="btn booking-card__wa"
              style={{ background: '#25d366', color: '#fff', justifyContent: 'center', textDecoration: 'none' }}
            >
              {t('booking.bookViaWa')}
            </a>

            <p className="booking-card__cancel">{t('booking.cancelFree')}</p>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="section section-alt">
          <div className="container">
            <h2 className="section-title" style={{ marginBottom: 32 }}>Similar Activities</h2>
            <div className="grid-3">
              {related.map(a => <ActivityCard key={a.id} activity={a} />)}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
